import { arrow, autoUpdate, computePosition, flip, offset, shift } from '@floating-ui/dom';
import { atLeast } from '../utils/breakpoints';
import { show } from './toggler';

// Definitions in story texts (functions/definitions.php): a highlighted term
// (.def-link, data-tooltip-content="#def-…") opens its definition, a hidden
// element on the page. From medium up in a tooltip next to the term, on small
// screens in the full-screen #def-overlay (closed by its [data-toggle] button).

const FADE = 350; // ms

const definitionFor = (link) => {
    const source = document.querySelector(link.dataset.tooltipContent);
    if (!source) return null;
    const copy = source.cloneNode(true);
    copy.removeAttribute('id');
    return copy;
};

let current = null; // {link, tip, stopUpdates}

function close() {
    if (!current) return;
    const {link, tip, stopUpdates} = current;
    current = null;
    link.setAttribute('aria-expanded', 'false');
    stopUpdates();
    tip.classList.remove('is-open');
    setTimeout(() => tip.remove(), FADE);
}

function openTooltip(link, content) {
    const tip = document.createElement('div');
    tip.className = 'def-tip';
    tip.setAttribute('role', 'tooltip');
    tip.innerHTML = '<div class="def-tip__box"><div class="def-tip__content"></div></div><span class="def-tip__arrow"></span>';
    tip.querySelector('.def-tip__content').append(content);
    document.body.append(tip);
    const arrowEl = tip.querySelector('.def-tip__arrow');

    // Above the term if there is room, else below, right, left; kept in the viewport.
    const position = () => computePosition(link, tip, {
        placement: 'top',
        middleware: [offset(6), flip({fallbackPlacements: ['bottom', 'right', 'left']}), shift(), arrow({element: arrowEl})],
    }).then(({x, y, placement, middlewareData}) => {
        tip.dataset.side = placement;
        // Whole-pixel left keeps the text crisp (as tooltipster did).
        Object.assign(tip.style, {left: `${Math.floor(x)}px`, top: `${y}px`});
        const {x: ax, y: ay} = middlewareData.arrow;
        Object.assign(arrowEl.style, {left: ax != null ? `${ax}px` : '', top: ay != null ? `${ay}px` : ''});
    });

    current = {link, tip, stopUpdates: autoUpdate(link, tip, position)};
    link.setAttribute('aria-expanded', 'true');
    requestAnimationFrame(() => tip.classList.add('is-open'));
}

function openOverlay(content) {
    const overlay = document.getElementById('def-overlay');
    if (!overlay) return;
    overlay.querySelector('.content').replaceChildren(content);
    show(overlay);
}

export default function definitions() {
    const links = document.querySelectorAll('.fn-link, .def-link');
    if (!links.length) return;

    links.forEach((link) => {
        link.setAttribute('aria-expanded', 'false');
        link.addEventListener('click', (e) => {
            e.stopPropagation();
            const same = current?.link === link;
            close();
            if (same) return;
            const content = definitionFor(link);
            if (!content) return;
            atLeast('medium') ? openTooltip(link, content) : openOverlay(content);
        });
    });

    // Close on a click outside the tooltip or Escape.
    document.addEventListener('click', (e) => {
        if (current && !current.tip.contains(e.target)) close();
    });
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') close();
    });
}
